import React from "react";
import { FilterOption } from "../FilterPopover.types";
import { useSelectFieldSubMenu } from "./SelectFieldSubMenu.hooks.ts";
import SubMenuList from "./SubMenuList.tsx";
import { DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import { IdNamePair } from "../../../../customLists/customListView/CustomListView.types.ts";

export interface SelectFieldSubMenuProps {
    selectedOption: FilterOption;
    onToggleSelect: (ids: Array<string>, options: Array<IdNamePair>) => void;
    onLeftArrowClick?: () => void;
    submenuInputRef: React.RefObject<HTMLInputElement>;
    filterBarData?: Array<IdNamePair>;
}

function SelectFieldSubMenu(props: SelectFieldSubMenuProps) {
    const { onToggleSelect, onLeftArrowClick, submenuInputRef, filterBarData } = props;
    const { isLoading, data, chosenValues, onSearchChange, search } = useSelectFieldSubMenu(props);

    const isServerSearched = props.selectedOption.entityType !== undefined || !!props.selectedOption.searchUrl;

    // Spread rather than rebuilding from id and name, so a value carrying an explanation keeps its info icon once
    // it is selected instead of losing it the moment it moves into the chosen list. The chip's resolved names take
    // precedence over the picker's list, so they are written last.
    const getChosenOptions = () => {
        const resolvedById = new Map<string, IdNamePair>();
        data.forEach((item) => resolvedById.set(item.id, item));
        filterBarData?.forEach((item) => resolvedById.set(item.id, item));

        return chosenValues.flatMap((id) => {
            const resolved = resolvedById.get(id);
            return resolved ? [{ ...resolved, id }] : [];
        });
    };

    return (
        <>
            {isLoading ? (
                <Box sx={{ p: "24px" }}>
                    <DotLegalFullScreenSpinner />
                </Box>
            ) : (
                <SubMenuList
                    data={data ?? []}
                    onChange={(clickedOption) => {
                        if (chosenValues.includes(clickedOption.id)) {
                            let newValues = chosenValues.filter((x) => x !== clickedOption.id);
                            let newFilterOptions = data.filter((x) => x.id !== clickedOption.id);
                            onToggleSelect(newValues, newFilterOptions);
                        } else if (props.selectedOption.singleSelect) {
                            onToggleSelect([clickedOption.id], [{ id: clickedOption.id, name: clickedOption.name }]);
                        } else {
                            const newChosenValues = [...chosenValues, clickedOption.id];
                            onToggleSelect(newChosenValues, [...(filterBarData ?? []), { id: clickedOption.id, name: clickedOption.name }]);
                        }
                    }}
                    onLeftArrowClick={onLeftArrowClick}
                    chosenValues={getChosenOptions()}
                    submenuInputRef={submenuInputRef}
                    onFilterChange={isServerSearched ? onSearchChange : undefined}
                    filterValue={isServerSearched ? search : undefined}
                    separateListForSelectedValue
                />
            )}
        </>
    );
}

export default SelectFieldSubMenu;
